import { useState } from "react";
import { useNavigate } from "react-router";

import { ClickAwayListener, Stack } from "@mui/material";

import { useClientsSearchContext } from "@/common/contexts/clients-search-context/ClientsSearchContext";
import { useCurrentUserContext } from "@/common/contexts/current-user-context/CurrentUserContext";
import { KnownKey } from "@/common/types/KnownKey";
import { mapClientsFilterFromSearchParams } from "@/pages/clients/clients/Clients.utils";
import { clientPath } from "@/router/ClientRoutes";

import { CLIENT_SMART_SEARCH_ARIA } from "./ClientSmartSearch.utils";
import { ClientListPopper } from "./components/client-list-popper/ClientListPopper";
import { useClientList } from "./components/client-list-popper/hooks/useClientList";
import { ClientSearchInput } from "./components/search-input/ClientSearchInput";
import { useAnchorWidth } from "./hooks/useAnchorWidth";
import { useSmartOpenClients } from "./hooks/useSmartOpenClients";

const NO_LAST_OPEN_CLIENT_HIGHLIGHTED = -1;

export function ClientSmartSearch() {
  const { currentUser } = useCurrentUserContext();
  const navigate = useNavigate();

  const { invalidateClientsQuery, handleFiltersUpdate } = useClientsSearchContext();

  const [searchValue, setSearchValue] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState<number>(NO_LAST_OPEN_CLIENT_HIGHLIGHTED);

  const { anchorEl, anchorWidth, updateAnchor } = useAnchorWidth<HTMLElement>();

  const { openLastOpenedClients, closeLastOpenedClients, updateOpen } = useSmartOpenClients();

  const { clients } = useClientList();

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    const root = e.currentTarget.closest(".MuiTextField-root");
    if (root instanceof HTMLElement) {
      updateAnchor(root);
      updateOpen(searchValue);
    }
  };

  const handleChange = (value: string) => {
    setSearchValue(value);
    updateOpen(value);
    setHighlightedIndex(NO_LAST_OPEN_CLIENT_HIGHLIGHTED);
  };

  const handleBlur = () => {
    closeLastOpenedClients();
    setHighlightedIndex(NO_LAST_OPEN_CLIENT_HIGHLIGHTED);
  };

  const triggerSearch = async () => {
    if (!searchValue.trim()) {
      return;
    }
    closeLastOpenedClients();
    const searchParams = clientPath.search(searchValue.trim());
    const newFilters = mapClientsFilterFromSearchParams(searchParams);
    await invalidateClientsQuery();
    await handleFiltersUpdate(newFilters);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (openLastOpenedClients) {
      handleOpenClientsKeyDown(e);
    } else {
      handleClosedClientsKeyDown(e);
    }
  };

  const handleOpenClientsKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const totalItems = clients.length + 1; // +1 for "MyClients"

    switch (e.key) {
      case KnownKey.ArrowDown:
        e.preventDefault();
        setHighlightedIndex((prev) => (prev < totalItems - 1 ? prev + 1 : 0));
        break;

      case KnownKey.ArrowUp:
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : totalItems - 1));
        break;

      case KnownKey.Enter:
        e.preventDefault();
        if (highlightedIndex === 0) {
          const searchParams = clientPath.my(currentUser.counselorId);
          void handleFiltersUpdate(mapClientsFilterFromSearchParams(searchParams));
          closeLastOpenedClients();
        } else {
          const id = clients[highlightedIndex - 1]?.id;
          if (id) {
            void navigate(clientPath.byId({ id }));
          }
          closeLastOpenedClients();
        }
        break;

      case KnownKey.Escape:
        e.preventDefault();
        closeLastOpenedClients();
        setHighlightedIndex(NO_LAST_OPEN_CLIENT_HIGHLIGHTED);
        break;

      default:
        break;
    }
  };

  const handleClosedClientsKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case KnownKey.ArrowDown:
        e.preventDefault();
        updateOpen(searchValue);
        break;

      case KnownKey.ArrowUp:
        e.preventDefault();
        updateOpen(searchValue);
        break;

      case KnownKey.Enter:
        e.preventDefault();
        void triggerSearch();
        break;
    }
  };

  return (
    <ClickAwayListener onClickAway={closeLastOpenedClients}>
      <Stack sx={{ flex: "1 1 100%" }}>
        <ClientSearchInput
          value={searchValue}
          onFocus={handleFocus}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onSearch={() => void triggerSearch()}
          onBlur={handleBlur}
          ariaExpanded={openLastOpenedClients}
          ariaControls={CLIENT_SMART_SEARCH_ARIA.listboxId}
          ariaActivedescendant={
            highlightedIndex >= 0 ? CLIENT_SMART_SEARCH_ARIA.getOptionId(highlightedIndex) : undefined
          }
        />

        <ClientListPopper
          clients={clients}
          anchorEl={anchorEl}
          open={openLastOpenedClients}
          width={anchorWidth}
          highlightedIndex={highlightedIndex}
        />
      </Stack>
    </ClickAwayListener>
  );
}
